import { IconButton, Modal } from '@material-ui/core'
import React from 'react'
import styled from 'styled-components'
import { mediaQueriesBreakpoints } from 'styles/mixins'
import TypographyPro from 'themes/TypographyPro'
import type { LocalizedCoupon } from 'types/Coupons'
import CloseIcon from '@material-ui/icons/Close'
import ImageNext from 'components/common/ImageNext'
import { getDefaultImgUrl } from 'mobx/CouponFlow/utils'
import { inject, observer } from 'mobx-react'
import type _Infra from 'mobx/Infra'
import Alert from 'components/common/Alert'

interface Props {
	coupon: LocalizedCoupon
	button?: JSX.Element
	info?: JSX.Element
	content?: JSX.Element
	onClose?: () => void
	error?: string | null
}

interface StoreProps {
	Infra: typeof _Infra
}

const Main = styled.div`
	background-color: var(--clear);
	width: 100%;
	height: 100%;

	position: fixed;
	inset: 0px;
	margin: auto;
	overflow: auto;

	@media ${mediaQueriesBreakpoints.md} {
		border-radius: var(--borderRadiusFrame);
		width: fit-content;
		height: fit-content;
		max-height: 90%;
	}
`

const Header = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 12px 10px 12px;
	position: sticky;
	background: white;
	top: 0;
	z-index: 10;

	@media ${mediaQueriesBreakpoints.mobile} {
		padding: 12px 20px 12px;
	}
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0 10px 10px;

	@media ${mediaQueriesBreakpoints.xs} {
		padding: 0 20px 20px;
	}

	@media ${mediaQueriesBreakpoints.lg} {
		width: 500px;
	}
	gap: 1.75rem;
`
const InfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	justify-content: center;

	width: 100%;
`

const ImageContainer = styled.div`
	position: relative;
	width: 100%;
	aspect-ratio: 191 / 100;
`

const Title = styled(TypographyPro).attrs({ variant: 'h4' })`
	color: var(--strokeMenuTitle);
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
`

const Description = styled(TypographyPro).attrs({ variant: 'BodyRegular' })`
	color: var(--strokeMenuTitle);
`

const Section = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`

const TitleDescriptionContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`

const ApplyContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	position: sticky;
	bottom: 0;
	width: 100%;
	background: var(--clear);
`

const BaseModal: React.FC<Props & StoreProps> = ({ coupon, onClose, Infra, info, content, button, error }) => (
	<Modal open>
		<Main data-testid="modal-coupon-container">
			<Header>
				<IconButton data-testid="close-modal" onClick={onClose} style={{ backgroundColor: 'transparent', padding: 0 }}>
					<CloseIcon />
				</IconButton>
			</Header>
			<Content>
				<InfoContainer>
					<ImageContainer data-testid="modal-image-container">
						<ImageNext src={coupon.img} fill alt="" fallbackSrc={getDefaultImgUrl(Infra)} data-testid="coupon-img" />
					</ImageContainer>
					<Section>
						<TitleDescriptionContainer>
							<Title data-testid="coupon-title">{coupon.title}</Title>
							<Description data-testid="coupon-description">{coupon.description}</Description>
						</TitleDescriptionContainer>
					</Section>
					{info}
				</InfoContainer>
				{content}
				<ApplyContainer data-testid="modal-apply-container">
					{error && <Alert variant="error" message={error} />}
					{!error && button}
				</ApplyContainer>
			</Content>
		</Main>
	</Modal>
)

export default inject('Infra')(observer(BaseModal)) as React.FC<Props>
